import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, useMeta, errMsg } from "@/lib/api";
import { ProofInput, uploadProof } from "@/components/admin/ProofDialog";

export function EventDialog({ open, onOpenChange, shipment, onSaved }) {
  const api = useApi();
  const { data: meta } = useMeta();
  const [f, setF] = useState({ status: "In Transit", location: "", note: "", timestamp: "", notify: true, reason: "" });
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    setPhoto(null);
    if (open && shipment) setF({ status: shipment.status, location: shipment.current_location?.name || "", note: "", timestamp: "", notify: true, reason: "" });
  }, [open, shipment]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const withPhoto = photo && f.status === "Delivered";
      const body = { ...f, notify: withPhoto ? false : f.notify, timestamp: f.timestamp ? new Date(f.timestamp).toISOString() : null };
      const r = await api("post", `/admin/shipments/${shipment.id}/events`, body);
      const em = r.email === "sent" ? " Receiver emailed." : r.email.startsWith("failed") ? ` Email ${r.email}.` : "";
      toast.success(`Status updated.${em}`);
      if (withPhoto) {
        try {
          const pr = await uploadProof(api, shipment.id, photo, f.notify);
          toast.success(pr.email === "sent" ? "Proof photo saved. Receiver emailed with the photo." : pr.email.startsWith("failed") ? `Proof photo saved. Email ${pr.email}.` : "Proof photo saved");
        } catch (pe) { toast.error(`Photo not saved${f.notify ? " and receiver not emailed" : ""}: ${errMsg(pe)}`); }
      }
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="event-dialog">
        <DialogHeader><DialogTitle className="font-display">Update status · <span className="font-mono text-[#FF6B00]">{shipment?.tracking_number}</span></DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="event-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.statuses || []).map((s) => <SelectItem key={s} value={s} data-testid={`event-status-option-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Location (city, country)</Label>
            <Input required value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} placeholder="e.g. Frankfurt, Germany" data-testid="event-location-input" />
            <p className="text-xs text-slate-500">The map pin is placed automatically from this location.</p>
          </div>
          <div className="space-y-1.5"><Label>Date & time (optional)</Label>
            <Input type="datetime-local" value={f.timestamp} onChange={(e) => setF({ ...f, timestamp: e.target.value })} data-testid="event-timestamp-input" />
          </div>
          <div className="space-y-1.5"><Label>Reason for change (kept in activity history)</Label>
            <Input maxLength={500} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="e.g. Scanned at hub" data-testid="event-reason-input" />
          </div>
          <div className="space-y-1.5"><Label>Note (optional)</Label>
            <Textarea value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="event-note-input" />
          </div>
          {f.status === "Delivered" && (
            <div className="space-y-1.5" data-testid="event-proof-section"><Label>Proof of delivery photo (optional)</Label>
              <ProofInput onChange={setPhoto} testId="event-proof-input" />
              <p className="text-xs text-slate-500">Shown on the tracking page and included in the receiver's Delivered email. JPG, PNG or WEBP, up to 8 MB.</p>
            </div>
          )}
          <div className="flex items-center justify-between border border-slate-200 rounded-sm p-3">
            <div><p className="text-sm font-medium">Email the receiver and customer</p><p className="text-xs text-slate-500">{shipment?.receiver?.email || "No receiver email on file"}</p></div>
            <Switch checked={f.notify} onCheckedChange={(v) => setF({ ...f, notify: v })} disabled={!shipment?.receiver?.email} data-testid="event-notify-switch" />
          </div>
          <Button type="submit" disabled={busy} className="w-full bg-[#FF6B00] hover:bg-[#E05D00]" data-testid="event-save-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Add checkpoint
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
